import { Page, PageHeader } from '../components/Page';
import { StatCard } from '../components/StatCard';
import { Donut } from '../components/Donut';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import {
  IcCreditCard,
  IcPill,
  IcTruck,
  IcBuilding,
  IcClock,
  IcCheck,
  IcDownload,
  IcCalendar,
} from '../components/icons';
import { Link } from 'react-router-dom';
import { useFetch } from '../lib/useFetch';
import { getSummary, getRevenue, type Summary } from '../lib/api';
import { FLOW_META, INVOICE_STATUS, type FlowType } from '../lib/types';
import { fmtCents0, fmtNum, fmtDate } from '../lib/format';
import { backState } from '../lib/backTo';

const BACK_TO_DASHBOARD = backState({ to: '/', label: 'Back to Dashboard' });

const RevenueTrend = () => {
  const state = useFetch(() => getRevenue(6));
  return (
    <div>
      <div className="section-title">Revenue trend — invoiced vs collected</div>
      <Async state={state} isEmpty={(d) => d.months.length === 0} emptyLabel="No revenue yet.">
        {(d) => {
          const max = Math.max(1, ...d.months.flatMap((m) => [m.invoicedCents, m.collectedCents]));
          return (
            <PCard style={{ padding: 20 }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', height: 150 }}>
                {d.months.map((m) => (
                  <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 120, width: '100%', justifyContent: 'center' }}>
                      <div title={`Invoiced ${fmtCents0(m.invoicedCents)}`} style={{ width: '40%', maxWidth: 26, height: `${(m.invoicedCents / max) * 100}%`, background: 'var(--p-primary,#2563eb)', borderRadius: '3px 3px 0 0' }} />
                      <div title={`Collected ${fmtCents0(m.collectedCents)}`} style={{ width: '40%', maxWidth: 26, height: `${(m.collectedCents / max) * 100}%`, background: 'var(--status-success-ink,#15803d)', borderRadius: '3px 3px 0 0' }} />
                    </div>
                    <div className="muted" style={{ fontSize: 11 }}>{m.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: 'var(--p-muted-fg)' }}>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--p-primary,#2563eb)', borderRadius: 2, marginRight: 5 }} />Invoiced</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--status-success-ink,#15803d)', borderRadius: 2, marginRight: 5 }} />Collected</span>
              </div>
            </PCard>
          );
        }}
      </Async>
    </div>
  );
};

const RunCard = ({ flow, nextRun }: { flow: FlowType; nextRun: string }) => {
  const meta = FLOW_META[flow];
  return (
    <PCard style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: meta.color }} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>{meta.label} fee</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--p-muted-fg)' }}>{meta.rails}</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--p-muted-fg)', marginTop: 2 }}>{meta.cadence}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, fontSize: 13 }}>
        <IcCalendar size={14} style={{ color: 'var(--p-muted-fg)' }} />
        Next run <strong style={{ fontWeight: 600 }}>{fmtDate(nextRun)}</strong>
      </div>
    </PCard>
  );
};

const Dashboard = ({ s }: { s: Summary }) => {
  const flowTotal = (f: FlowType) => s.byFlow.find((b) => b.flow === f)?.totalCents ?? 0;
  return (
    <>
      <div className="grid grid-6">
        <StatCard tone="in-progress" icon={IcCreditCard} label="Platform" value={fmtCents0(flowTotal('platform'))} />
        <StatCard tone="success" icon={IcPill} label="Pharmacy" value={fmtCents0(flowTotal('pharmacy'))} />
        <StatCard tone="in-transit" icon={IcTruck} label="Shipping" value={fmtCents0(flowTotal('shipping'))} />
        <StatCard tone="staged" icon={IcBuilding} label="Clients" value={fmtNum(s.clientCount)} />
        <StatCard tone="attention" icon={IcClock} label="Outstanding" value={fmtCents0(s.outstandingCents)} sub={`${s.openInvoiceCount} open`} />
        <StatCard tone="delivered" icon={IcCheck} label="Collected (mo)" value={fmtCents0(s.collectedThisMonthCents)} />
      </div>

      <div className="grid grid-2">
        <PCard style={{ padding: 20 }}>
          <div className="section-title">Revenue by fee type</div>
          {s.byFlow.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>No invoices yet.</p>
          ) : (
            <Donut
              segments={s.byFlow.map((b) => ({
                label: FLOW_META[b.flow].label,
                value: b.totalCents,
                color: FLOW_META[b.flow].color,
                display: fmtCents0(b.totalCents),
              }))}
            />
          )}
        </PCard>
        <div>
          <div className="section-title">Upcoming billing runs</div>
          <div className="grid" style={{ gridTemplateColumns: '1fr' }}>
            {s.upcomingRuns.map((r) => (
              <RunCard key={r.flow} flow={r.flow} nextRun={r.nextRun} />
            ))}
          </div>
        </div>
      </div>

      <RevenueTrend />

      <div>
        <div className="section-title">Recent invoices</div>
        <PCard>
          {s.recentInvoices.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--p-muted-fg)', padding: 16, margin: 0 }}>
              No invoices yet. Trigger a billing run to populate.
            </p>
          ) : (
            <table className="p-table">
              <thead>
                <tr>
                  {['Invoice', 'Client', 'Type', 'Amount', 'Status', 'Due'].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {s.recentInvoices.map((inv) => {
                  const st = INVOICE_STATUS[inv.status];
                  return (
                    <tr key={inv._id}>
                      <td className="mono"><Link to={`/invoices/${inv._id}`} state={BACK_TO_DASHBOARD}>#{inv._id.slice(-6)}</Link></td>
                      <td>{inv.clientName}</td>
                      <td className="muted">{FLOW_META[inv.flow].label}</td>
                      <td className="tabular">{fmtCents0(inv.totalCents)}</td>
                      <td>
                        <StatusPill tone={st.tone}>{st.label}</StatusPill>
                      </td>
                      <td className="muted">{inv.dueAt ? fmtDate(inv.dueAt) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </PCard>
      </div>
    </>
  );
};

export const DashboardPage = () => {
  const state = useFetch(getSummary);
  return (
    <Page>
      <PageHeader
        title="Dashboard"
        sub="Billing overview"
        actions={
          <>
            <button className="btn btn--ghost">
              <IcDownload size={14} /> Export
            </button>
            <button className="btn btn--primary">New invoice</button>
          </>
        }
      />
      <Async state={state}>{(s) => <Dashboard s={s} />}</Async>
    </Page>
  );
};
